import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, List, ListItem, ListItemText, ListItemIcon,
  Divider, TextField, Button, LinearProgress, Chip,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SettingsIcon from '@mui/icons-material/Settings';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getAuthHeaders } from '../../supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Settings() {
  const [cronStatus, setCronStatus] = useState(null);
  const [cronFrequency, setCronFrequency] = useState(30);
  const [cronSaving, setCronSaving] = useState(false);
  const [cronRunning, setCronRunning] = useState(false);
  const cronPollingRef = useRef(null);

  const fetchCronStatus = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/cron/status`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCronStatus(data);
        setCronFrequency(data.frequencyDays ?? 30);
        setCronRunning(data.running);
      }
    } catch (err) {
      console.error('Failed to fetch cron status:', err);
    }
  }, []);

  useEffect(() => {
    fetchCronStatus();
  }, [fetchCronStatus]);

  // Poll while running
  useEffect(() => {
    if (cronRunning) {
      cronPollingRef.current = setInterval(fetchCronStatus, 3000);
    } else if (cronPollingRef.current) {
      clearInterval(cronPollingRef.current);
      cronPollingRef.current = null;
    }
    return () => {
      if (cronPollingRef.current) clearInterval(cronPollingRef.current);
    };
  }, [cronRunning, fetchCronStatus]);

  const handleSaveCronFrequency = async () => {
    setCronSaving(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/cron/frequency`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ days: cronFrequency }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to save frequency');
      }
      await fetchCronStatus();
    } catch (err) {
      console.error('Failed to save frequency:', err);
      alert('Failed to save frequency');
    }
    setCronSaving(false);
  };

  const handleRunCronJob = async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/cron/run`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to start cron job');
        return;
      }
      setCronRunning(true);
      fetchCronStatus();
    } catch (err) {
      console.error('Failed to run cron job:', err);
      alert('Failed to start cron job');
    }
  };

  const cronProgress = cronStatus?.total > 0
    ? Math.round((cronStatus.processed / cronStatus.total) * 100)
    : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          backgroundColor: '#f9f9f9',
          borderRadius: 2,
          minHeight: '80vh',
        }}
      >
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Settings
        </Typography>

        <List>
          <ListItem button>
            <ListItemIcon>
              <EmailIcon />
            </ListItemIcon>
            <ListItemText
              primary="Email Templates"
              secondary="Manage outreach email templates"
            />
          </ListItem>
          <Divider />
          <ListItem button>
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="General Settings"
              secondary="Configure application settings"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          Note: This section is under development. More settings will be added soon.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Cron Job Section */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ScheduleIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Bulk Free Check — Cron Job
            </Typography>
          </Box>

          <Paper
            variant="outlined"
            sx={{ p: 3, borderRadius: 2, backgroundColor: '#fff' }}
          >
            {/* Frequency setting */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
              <TextField
                label="Frequency (days)"
                type="number"
                size="small"
                value={cronFrequency}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setCronFrequency(isNaN(val) || val < 0 ? 0 : val);
                }}
                inputProps={{ min: 0 }}
                sx={{ width: 140 }}
                helperText={cronFrequency === 0 ? 'Disabled' : `Every ${cronFrequency} day(s)`}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleSaveCronFrequency}
                  disabled={cronSaving || cronStatus?.frequencyDays === cronFrequency}
                  sx={{ textTransform: 'none', fontWeight: 600, height: 40 }}
                >
                  {cronSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleRunCronJob}
                  disabled={cronRunning}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    height: 40,
                    backgroundColor: '#121212',
                    '&:hover': { backgroundColor: '#333' },
                  }}
                >
                  {cronRunning ? 'Running...' : 'Run Now'}
                </Button>
              </Box>
            </Box>

            {/* Progress */}
            {cronRunning && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Processing influencers...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {cronStatus?.processed ?? 0} / {cronStatus?.total ?? 0} ({cronProgress}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant={cronStatus?.total > 0 ? 'determinate' : 'indeterminate'}
                  value={cronProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': { backgroundColor: '#121212', borderRadius: 4 },
                  }}
                />
              </Box>
            )}

            {/* Last run info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Last run:
              </Typography>
              {cronStatus?.lastRun ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                    label={new Date(cronStatus.lastRun).toLocaleString()}
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                  {cronStatus.lastResult && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      — {cronStatus.lastResult}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Never
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
}
